import { useCallback } from 'react';
import type { OrganizationMembershipResource } from '@clerk/types';
import { MAX_VISIBLE_MERCHANTS } from '../config';
import { useLocalStorage } from '@uidotdev/usehooks';

interface RecentMerchant {
  id: string;
  slug: string;
  name: string;
  imageUrl: string;
  lastAccessed: number;
}

export const useRecentMerchants = () => {
  const [recentMerchants, setRecentMerchants] = useLocalStorage<RecentMerchant[]>(
    'recent-merchants',
    []
  );

  const addRecentMerchant = useCallback(
    (membership: OrganizationMembershipResource) => {
      const newMerchant: RecentMerchant = {
        id: membership.organization.id,
        slug: membership.organization.slug!,
        name: membership.organization.name,
        imageUrl: membership.organization.imageUrl,
        lastAccessed: Date.now()
      };

      // Remove if already exists and add to beginning
      const filtered = recentMerchants.filter((m) => m.id !== newMerchant.id);
      const updated = [newMerchant, ...filtered].slice(0, MAX_VISIBLE_MERCHANTS);

      setRecentMerchants(updated);
    },
    [recentMerchants, setRecentMerchants]
  );

  const getSortedMerchants = useCallback(
    (allMemberships: OrganizationMembershipResource[], currentOrgId?: string) => {
      // Create a map of recent merchants for quick lookup
      const recentMap = new Map(recentMerchants.map((m) => [m.id, m]));

      // We need to separate the active merchant from the recent merchants
      // That way if it syncs across tabs, the active merchant is always at the top,
      // and it doesn't ever get removed from the list
      const activeMerchant: OrganizationMembershipResource[] = [];
      const recentMerchantsList: OrganizationMembershipResource[] = [];
      const otherMerchants: OrganizationMembershipResource[] = [];

      allMemberships.forEach((membership) => {
        if (currentOrgId && membership.organization.id === currentOrgId) {
          activeMerchant.push(membership);
        } else if (recentMap.has(membership.organization.id)) {
          recentMerchantsList.push(membership);
        } else {
          otherMerchants.push(membership);
        }
      });

      recentMerchantsList.sort((a, b) => {
        const aRecent = recentMap.get(a.organization.id);
        const bRecent = recentMap.get(b.organization.id);
        return (bRecent?.lastAccessed ?? 0) - (aRecent?.lastAccessed ?? 0);
      });

      // Return: active first, then recent, then others, but limit total to MAX_VISIBLE_MERCHANTS
      return [...activeMerchant, ...recentMerchantsList, ...otherMerchants];
    },
    [recentMerchants]
  );

  return { addRecentMerchant, getSortedMerchants };
};
